import { useEffect, useCallback, useState, Suspense, useLayoutEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';

function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

import './scss/app.scss';
import { authenticationService } from './services/authenticationService';
import { apiService } from './services/apiService';
import { loginStatusPollingService } from './services/loginStatusPollingService';

import HomeRouter from "./components/HomeRouter";
import PrivateRoute from './hooks/PrivateRoute';
import SignUpRoute from './hooks/SignUpRoute';
import ErrorModal from './templates/ErrorModal';
import Loader from './templates/Loader';
import { useTeamsCache } from "./hooks/useTeamsCache";
import { loadFontAwesome } from "./helpers/loadFontAwesome";
import lazyWithRetry from "./helpers/lazyWithRetry";
import useBuildVersionGuard from './hooks/useBuildVersionGuard';

// All page-level components are lazy-loaded so the initial bundle only ships
// what's needed for the current route. Each route renders inside the shared
// <Suspense> boundary below.
const Home = lazyWithRetry(() => import("./pages/Home"));
const Login = lazyWithRetry(() => import('./pages/Account/Login'));
const LoggedOutLandingPremierLeague = lazyWithRetry(() => import('./pages/LoggedOutHome/LoggedOutLandingPremierLeague'));
const LoggedOutLandingLaLiga = lazyWithRetry(() => import('./pages/LoggedOutHome/LoggedOutLandingLaLiga'));
const LaLigaThankYou = lazyWithRetry(() => import('./templates/LaLigaThankYou'));
const TrialExpired = lazyWithRetry(() => import("./pages/Account/TrialExpired"));
const Subscribe = lazyWithRetry(() => import("./pages/Subscribe"));
const CreditCardTrial = lazyWithRetry(() => import("./pages/CreditCardTrial"));
const TrialSignUp = lazyWithRetry(() => import("./pages/Onboarding/TrialSignUp"));

const PlayerDetail = lazyWithRetry(() => import('./pages/PlayerDetail'));
const PlayerNews = lazyWithRetry(() => import('./pages/PlayerNews'));
const TeamReport = lazyWithRetry(() => import('./pages/TeamReport'));
const NewsFeed = lazyWithRetry(() => import('./pages/NewsFeed/NewsFeed'));
const ManagePlayersWatchlist = lazyWithRetry(() => import('./pages/NewsFeed/ManagePlayersWatchlist'));
const PlayerFilterListing = lazyWithRetry(() => import('./pages/PlayerFilterListing'));
const PerfectPlayer = lazyWithRetry(() => import('./pages/PerfectPlayer'));
const ScoutAssistBasic = lazyWithRetry(() => import('./pages/ScoutAssistBasic'));
const ScoutAssistChat = lazyWithRetry(() => import('./pages/ScoutAssist'));
const ManageSavedNews = lazyWithRetry(() => import('./pages/NewsFeed/ManageSavedNews'));
const ManageSavedSearches = lazyWithRetry(() => import('./pages/NewsFeed/ManageSavedSearches'));
const ManageSavedPlayerGPTSearches = lazyWithRetry(() => import('./pages/PlayerGPT/ManageSavedSearches'));
const AIInsightsListing = lazyWithRetry(() => import('./pages/AIInsightsListing'));
const Reports = lazyWithRetry(() => import('./pages/Reports'));
const SearchTransferReport = lazyWithRetry(() => import('./pages/SearchTransferReport'));
const TsAndCs = lazyWithRetry(() => import("./pages/TsAndCs"));
const ForgottenPassword = lazyWithRetry(() => import("./pages/Account/ForgottenPassword"));
const UpdatePersonalDetails = lazyWithRetry(() => import("./pages/Account/UpdatePersonalDetails"));
const ResetPassword = lazyWithRetry(() => import("./pages/Account/ResetPassword"));
const ResetPasswordAction = lazyWithRetry(() => import("./pages/Account/ResetPasswordAction"));
const AuthCallback = lazyWithRetry(() => import("./pages/Account/AuthCallback"));
const Account = lazyWithRetry(() => import("./pages/Account/Account"));
const NotFound = lazyWithRetry(() => import("./pages/Errors/NotFound"));
const Enquiry = lazyWithRetry(() => import("./pages/Enquiry"));
const Feedback = lazyWithRetry(() => import("./pages/Feedback"));
const TeamsListing = lazyWithRetry(() => import('./pages/TeamsListing'));
const PlayerListing = lazyWithRetry(() => import('./pages/PlayerListing'));
const DemandGenLandingPage = lazyWithRetry(() => import('./pages/Campaigns/DemandGenLandingPage'));
const AffiliateLandingPage = lazyWithRetry(() => import('./pages/Campaigns/AffiliateLandingPage'));
const SquadAnalysis = lazyWithRetry(() => import('./pages/Account/SquadAnalysis'));
const FixturesListing = lazyWithRetry(() => import('./pages/FixturesListing'));
const FremiumFixtures = lazyWithRetry(() => import('./pages/FremiumFixtures'));
const FremiumPlayers = lazyWithRetry(() => import('./pages/FremiumPlayers'));
const Notification = lazyWithRetry(() => import('./pages/Account/Notification'));
const NotificationList = lazyWithRetry(() => import('./pages/Account/NotificationList'));
const HowItWorks = lazyWithRetry(() => import('./pages/HowItWorks'));
const NewsDetail = lazyWithRetry(() => import('./pages/NewsDetail'));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const CancellationPolicy = lazyWithRetry(() => import("./pages/CancellationPolicy"));
const AboutUs = lazyWithRetry(() => import("./pages/AboutUs"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const TipRecord = lazyWithRetry(() => import("./pages/TipRecord"));
const AiFootballPredictions = lazyWithRetry(() => import("./pages/Guides/AiFootballPredictions"));
const WhatIsBtts = lazyWithRetry(() => import("./pages/Guides/WhatIsBtts"));
const HowAiSportsIntelligenceWorks = lazyWithRetry(() => import("./pages/Guides/HowAiSportsIntelligenceWorks"));
const ExpectedGoalsXgAi = lazyWithRetry(() => import("./pages/Guides/ExpectedGoalsXgAi"));
const CanAiPredictFootball = lazyWithRetry(() => import("./pages/Guides/CanAiPredictFootball"));
const AiVsHumanPredictions = lazyWithRetry(() => import("./pages/Guides/AiVsHumanPredictions"));
const FootballPredictionAccuracy = lazyWithRetry(() => import("./pages/Guides/FootballPredictionAccuracy"));

// Redirect component that preserves query parameters
const SubscribeMonthlyRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/subscribe${location.search}`} replace />;
};

// Redirect component that preserves query parameters
const AIInsightsRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/ai-summaries${location.search}`} replace />;
};

// 301-equivalent redirect for the legacy news-article URL format
// (/news-article/:fixtureId/:slug?newsId=X) to the current one
// (/news-article/:fixtureId/:newsId/:slug). Renders NewsDetail directly
// when there's no newsId to migrate.
const NewsArticleLegacyRedirect = () => {
  const { fixtureId, slug } = useParams();
  const location = useLocation();
  const newsId = new URLSearchParams(location.search).get('newsId');
  
  if (newsId) {
    return <Navigate to={`/news-article/${fixtureId}/${newsId}/${slug}`} replace />;
  }

  return <NewsDetail />;
};


function App() {

 // debugger;

  // Prefetch teams data on app load for better performance


  const [errorModalMessage, setErrorModalMessage] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const onError = useCallback((message) => { setShowErrorModal(true); setErrorModalMessage(message) }, []);


  useTeamsCache();

  // Reloads at the next navigation if this tab is running a build that has since been
  // replaced, so it never requests a chunk that the current deploy no longer serves.
  useBuildVersionGuard();

  // PostHog is loaded by GTM and exposes a global `window.posthog`. Its snippet
  // queues identify/people.set calls until init completes, so calling through
  // optional chaining is safe even if GTM hasn't injected it yet.
  useEffect(() => {
    const subscription = authenticationService.currentUser.subscribe(user => {
      if (!user) return;
      try {
        window.posthog?.identify?.(user.email);
        window.posthog?.people?.set?.({
          email: user.email,
          name: user.name,
          is_logged_in: true,
        });
      } catch (error) {
        console.error('PostHog identify error:', error);
      }
    });
    return () => subscription.unsubscribe();
  }, []);


  // Load Font Awesome off the critical render path (see loadFontAwesome.js).
  useEffect(() => {
    loadFontAwesome();
  }, []);

  // Initialize login status polling
  useEffect(() => {
    loginStatusPollingService.init();

    return () => {
      loginStatusPollingService.destroy();
    };
  }, []);

  // Check for customer query parameter and redirect to Stripe portal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const customerParam = urlParams.get('customer');

    if (customerParam) {
      // Navigate to Stripe portal
      let data = new FormData();
      data.append('customerId', customerParam);
      // Remove the customer param from the return URL to avoid infinite loop
      const returnUrl = window.location.href.split('?')[0];
      data.append('returnUrl', returnUrl);

      apiService.apiPostForm(`${import.meta.env.VITE_API_URL}/subscribe/createCustomerPortalSession`, data).then((resp) => {
        if (resp && resp.url) {
          window.location.href = resp.url;

        } else {
          onError('Failed to redirect to Stripe portal');
        }
      },
        error => {
          onError(error);
        });
    }
  }, [onError]);


  return (
    <>
      {showErrorModal &&
        <ErrorModal message={errorModalMessage} onClose={() => setShowErrorModal(false)} />
      }
      <ScrollToTop />
      <Suspense fallback={<div className="vh-100 center-content"><Loader /></div>}>
 
        <Routes>
          <Route path="/" element={<HomeRouter />} />
          {/* World Cup 2026 landing retired — redirect any inbound links to the Premier League page */}
          <Route path="/world-cup-2026" element={<Navigate to="/premier-league" replace />} />
          <Route path="/premier-league" element={<LoggedOutLandingPremierLeague />} />
          <Route path="/la-liga" element={<LoggedOutLandingLaLiga />} />
          <Route path="/la-liga/thankyou" element={<LaLigaThankYou />} />
          <Route path="/ai-football-analysis" element={<DemandGenLandingPage />} />
          <Route path="/affiliate-campaign" element={<AffiliateLandingPage />} />

          
          <Route path="/dashboard" element={<PrivateRoute element={Home} />} />
          <Route path="/reports/fixture-analysis" element={<PrivateRoute element={SquadAnalysis} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/signInCallBack" element={<AuthCallback />} />
          <Route path="/forgotten-password" element={<ForgottenPassword />} />
          <Route path="/password/reset/:token" element={<ResetPasswordAction />} />
          <Route path="/account/reset-password" element={<PrivateRoute element={ResetPassword} skipSubscriptionCheck />} />
          <Route path="/account/personal-details" element={<PrivateRoute element={UpdatePersonalDetails} skipSubscriptionCheck />} />
          <Route path="/fixtures-listing" element={<PrivateRoute element={FixturesListing} />} />
          <Route path="/fixtures-preview" element={<FremiumFixtures />} />
          <Route path="/fixtures-preview/:fixtureId" element={<FremiumFixtures />} />
          <Route path="/fixtures-preview/:fixtureId/:slug" element={<FremiumFixtures />} />
          <Route path="/player-preview/:slug" element={<FremiumPlayers />} />
          <Route path="/trial-expired" element={<TrialExpired />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/creditcardtrial" element={<CreditCardTrial />} />
          <Route path="/subscribe/monthly" element={<SubscribeMonthlyRedirect />} />

          {/* SignUpRoute sends an already-registered user to the payment step rather
              than the email/password card, so every /sign-up link on the site lands
              them in the right place without resolving the destination per link. */}
          <Route path="/sign-up" element={<SignUpRoute element={TrialSignUp} />} />
          <Route path="/sign-up/add-password" element={<SignUpRoute element={TrialSignUp} />} />
          <Route path="/sign-up/finish" element={<PrivateRoute element={TrialSignUp} skipSubscriptionCheck />} />
          <Route path="/sign-up/plan" element={<PrivateRoute element={TrialSignUp} skipSubscriptionCheck />} />
          <Route path="/sign-up/add-payment-method" element={<PrivateRoute element={TrialSignUp} skipSubscriptionCheck />} />
          <Route path="/sign-up/success" element={<TrialSignUp />} />
          <Route path="/sign-up/add-teams-competitions" element={<TrialSignUp />} />
          <Route path="/sign-up/method" element={<TrialSignUp />} />

          <Route path="/account" element={<PrivateRoute element={Account} skipSubscriptionCheck />} />
          <Route path="/account/notification" element={<PrivateRoute element={Notification} skipSubscriptionCheck />} />
          <Route path="/account/notification/inbox" element={<PrivateRoute element={NotificationList} skipSubscriptionCheck />} />
          <Route path="/playernews" element={<PrivateRoute element={PlayerNews} requiredPermissions={['sportskinetic-hidden-for-now']} />} />
          <Route path="/reports" element={<PrivateRoute element={Reports} requiredPermissions={['sportskinetic-access-reports']} />} />
          <Route path="/reports/team-transfer-analysis" element={<PrivateRoute element={SearchTransferReport} requiredPermissions={['sportskinetic-access-reports']} />} />
          <Route path="/reports/team-transfer-analysis/:id" element={<PrivateRoute element={TeamReport} requiredPermissions={['sportskinetic-access-reports']} />} />
          <Route path="/my-news" element={<PrivateRoute element={NewsFeed} requiredPermissions={['sportskinetic-access-news-and-insights']} />} />
          <Route path="/news-article/:fixtureId/:newsId/:slug" element={<NewsDetail />} />
          <Route path="/news-article/:fixtureId/:slug?" element={<NewsArticleLegacyRedirect />} />
          <Route path="/saved/following/:tab" element={<PrivateRoute element={ManagePlayersWatchlist} requiredPermissions={['sportskinetic-watchlist', 'sportskinetic-watchlist-limited']} />} />
          <Route path="/saved/news" element={<PrivateRoute element={ManageSavedNews} requiredPermissions={['sportskinetic-save-articles']} />} />
          <Route path="/saved/searches" element={<PrivateRoute element={ManageSavedSearches} requiredPermissions={['sportskinetic-save-searches', 'sportskinetic-save-searches-limited']} />} />
          <Route path="/saved/playergpt" element={<PrivateRoute element={ManageSavedPlayerGPTSearches} />} />
          <Route path="/track-record" element={<TipRecord />} />
          <Route path="/playerdetail/:slug" element={<PrivateRoute element={PlayerDetail} />} />
          <Route path="/teams-listing" element={<TeamsListing />} />
          <Route path="/teams-listing/:id" element={<PlayerListing />} />
          <Route path="/listing/ones-to-watch" element={<PrivateRoute element={PlayerFilterListing} requiredPermissions={['sportskinetic-access-ones-to-watch']} />} />
          <Route path="/ai-insights" element={<AIInsightsRedirect />} />
          <Route path="/ai-summaries" element={<PrivateRoute element={AIInsightsListing} requiredPermissions={['sportskinetic-access-ai-insights']} />} />
          <Route path="/ai-summaries/:tab" element={<PrivateRoute element={AIInsightsListing} requiredPermissions={['sportskinetic-access-ai-insights']} />} />
          <Route path="/perfectplayer" element={<PrivateRoute element={PerfectPlayer} requiredPermissions={['sportskinetic-access-scout-assist']} />} />
          <Route path="/playergpt_basic/:example" element={<PrivateRoute element={ScoutAssistBasic} requiredPermissions={['sportskinetic-access-scout-assist']} />} />
          <Route path="/playergpt/" element={<ScoutAssistChat />} />
          <Route path="/terms-and-conditions" element={<TsAndCs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/guides/ai-football-predictions" element={<AiFootballPredictions />} />
          <Route path="/guides/what-is-btts" element={<WhatIsBtts />} />
          <Route path="/guides/how-ai-sports-intelligence-works" element={<HowAiSportsIntelligenceWorks />} />
          <Route path="/guides/expected-goals-xg-ai" element={<ExpectedGoalsXgAi />} />
          <Route path="/guides/can-ai-predict-football" element={<CanAiPredictFootball />} />
          <Route path="/guides/ai-vs-human-predictions" element={<AiVsHumanPredictions />} />
          <Route path="/guides/football-prediction-accuracy" element={<FootballPredictionAccuracy />} />
          <Route path="/contact-us" element={<Enquiry />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
